import { readAnalyzeExternalWebFailureMessage } from '@/lib/assistant/analyze-external-web-result-contract';

export const TOOL_STATUS = {
  inputStreaming: 'input-streaming',
  inputAvailable: 'input-available',
  outputAvailable: 'output-available',
  failed: 'failed',
} as const;

export type ToolState = (typeof TOOL_STATUS)[keyof typeof TOOL_STATUS];

export type ToolPhase = 'pending' | 'running' | 'completed' | 'failed';

export type RunningLabelValues = Record<ToolPhase, string>;

const TOOL_STATE_PHASE: Record<ToolState, ToolPhase> = {
  [TOOL_STATUS.inputStreaming]: 'pending',
  [TOOL_STATUS.inputAvailable]: 'running',
  [TOOL_STATUS.outputAvailable]: 'completed',
  [TOOL_STATUS.failed]: 'failed',
};

export function getToolPhase(state: ToolState): ToolPhase {
  return TOOL_STATE_PHASE[state];
}

export function getRunningLabel(state: ToolState, values: RunningLabelValues): string {
  return values[getToolPhase(state)];
}

export function resolveToolState({
  error,
  output,
  hasInput = false,
  fallbackState = TOOL_STATUS.inputStreaming,
}: {
  error?: unknown;
  output?: unknown;
  hasInput?: boolean;
  fallbackState?: ToolState;
}): ToolState {
  const hasError =
    typeof error === 'string' ? error.trim().length > 0 : error !== undefined && error !== null;

  if (hasError) {
    return TOOL_STATUS.failed;
  }

  if (output !== undefined) {
    return TOOL_STATUS.outputAvailable;
  }

  if (hasInput) {
    return TOOL_STATUS.inputAvailable;
  }

  return fallbackState;
}

export function resolveToolStateFromMessage(msg: {
  toolResult?: { rawOutput?: unknown };
  toolError?: string;
  toolInput?: Record<string, unknown>;
  toolArgsAccumulated?: string;
}): ToolState {
  const analyzeExternalWebFailure = readAnalyzeExternalWebFailureMessage(msg.toolResult?.rawOutput);

  return resolveToolState({
    error: msg.toolError ?? analyzeExternalWebFailure,
    output: msg.toolResult?.rawOutput,
    hasInput: msg.toolInput !== undefined || Boolean(msg.toolArgsAccumulated?.trim()),
    fallbackState: TOOL_STATUS.inputStreaming,
  });
}

export function resolveToolStateFromDetail(detail: {
  output?: unknown;
  error?: string;
}): ToolState {
  return resolveToolState({
    error: detail.error,
    output: detail.output,
    fallbackState: TOOL_STATUS.inputAvailable,
  });
}
